import React, { FC, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  Collapse,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { ExpandMore, ExpandLess, UnfoldMore } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import CustomLoading from "./CustomLoading";
import { TrashIcon } from "./icons";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import ConfirmDialog from "./ConfirmDialog";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: 13,
  color: "#374151",
  backgroundColor: "#ffffff !important",
  borderBottom: "2px solid #f3f4f6",
  whiteSpace: "nowrap",
  padding: "6px 10px",
}));

const SelectedRow = styled(TableRow)(({ theme }) => ({
  "&.Mui-selected": {
    backgroundColor: theme.palette.action.selected,
  },
}));

interface IColumn {
  id: string;
  label: string;
  width?: string;
  render?: any;
}

interface CustomTableProps {
  columns: IColumn[];
  data: any[];
  totalCount: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number
  ) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRowClick?: (row: any) => void;
  collapsible?: boolean;
  selectable?: boolean;
  sortable?: boolean;
  collapsibleContent?: (row: any) => React.ReactNode;
  statusFunction: (row: any) => void;
  onDetailClick?: (row: any) => void;
  onNavigationDetailClick?: (row: any) => void;
  onRowDetailClick?: (row: any) => void;
  maxWidth?: string;
  maxHeight?: string;
  isPagination?: boolean;
  onRowDeleteClick?: (row: any) => void;
  showActions: boolean;
  loading?: boolean;
  isDetailLoading?: boolean;
  paper?: boolean;
  isBorderRadius?: boolean;
  // sensorAlarm?: any;
}

const CustomTable: FC<CustomTableProps> = ({
  columns,
  data,
  loading = false,
  totalCount,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onRowClick,
  collapsible,
  selectable,
  sortable = false,
  collapsibleContent,
  statusFunction,
  isPagination = true,
  showActions = "true",
  paper = true,
  isDetailLoading,
  maxHeight = "50rem",
}) => {
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState<string>(
    sortable ? columns[0]?.id ?? "" : ""
  );
  const [selected, setSelected] = useState<number[]>([]);
  const [open, setOpen] = useState<Record<number, boolean>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(data.map((row) => row.id));
    } else {
      setSelected([]);
    }
  };

  const handleConfirmDelete = () => {
    statusFunction(selectedRow);
    setConfirmOpen(false);
  };

  const handleDeleteClick = (row: any) => {
    setSelectedRow(row);
    setConfirmOpen(true);
  };

  const handleClick = (event: React.MouseEvent<unknown>, id: number) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = selected.filter((item) => item !== id);
    }

    setSelected(newSelected);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | any,
    newPage: number
  ) => {
    onPageChange(event, newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onRowsPerPageChange(event);
  };

  const isSelected = (id: number) => selected.indexOf(id) !== -1;

  const sortedData = sortable && orderBy
    ? [...data].sort((a, b) => {
        const aVal = a[orderBy] ?? "";
        const bVal = b[orderBy] ?? "";
        if (aVal < bVal) return order === "asc" ? -1 : 1;
        if (aVal > bVal) return order === "asc" ? 1 : -1;
        return 0;
      })
    : data;

  if (loading) {
    return (
      <div style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "50vh",
        width: "100%"
      }}>
        <CustomLoading
          type="BallTriangle"
          color="#4fa94d"
          height={50}
          width={50}
        />
      </div>
    );
  }

  const CustomPagination = ({ page, count, onPageChange }: any) => {
    const handleBackButtonClick = (event: any) => {
      if (page > 1) {
        onPageChange(event, page - 1);
      }
    };

    const handleNextButtonClick = (event: any) => {
      if (page < count) {
        onPageChange(event, page + 1);
      }
    };

    return (
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="flex-end"
        display="flex"
        sx={{ flexWrap: "nowrap", flexShrink: 0 }}
      >
        <IconButton
          size="small"
          onClick={handleBackButtonClick}
          disabled={page === 1}
        >
          <ArrowBackIosIcon
            fontSize="small"
            sx={{ color: page === 1 ? "#d1d5db" : "var(--color-primary)", fontSize: "1rem" }}
          />
          <Typography
            variant="body2"
            sx={{
              color: page === 1 ? "#d1d5db" : "var(--color-primary)",
              marginLeft: 0.5,
            }}
          >
            {"Geri"}
          </Typography>
        </IconButton>
        <Pagination
          page={page}
          count={count}
          onChange={onPageChange}
          shape="rounded"
          hideNextButton
          hidePrevButton
          siblingCount={1}
          boundaryCount={1}
          color="primary"
        />
        <IconButton onClick={handleNextButtonClick} disabled={page === count}>
          <Typography
            variant="body2"
            sx={{ color: page === count ? "#d1d5db" : "var(--color-primary)", marginRight: 1 }}
          >
            {"İleri"}
          </Typography>
          <ArrowForwardIosIcon
            fontSize="small"
            sx={{
              color: page === count ? "#d1d5db" : "var(--color-primary)",
              fontSize: "1rem",
            }}
          />
        </IconButton>
      </Stack>
    );
  };

  return (
    <>
      <TableContainer
        component={paper ? Paper : "div"}
        sx={{ maxHeight, overflow: "auto", border: "1px solid #f3f4f6", borderRadius: "10px !important", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
      >
        <Table size="small" stickyHeader sx={{ tableLayout: "fixed", minWidth: 500, width: "100%" }}>
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={
                      selected.length > 0 && selected.length < data.length
                    }
                    checked={data.length > 0 && selected.length === data.length}
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
              )}
              {collapsible && <TableCell padding="checkbox"></TableCell>}

              {columns.map((column) => (
                <StyledTableCell
                  key={column.id}
                  sortDirection={
                    sortable && orderBy === column.id ? order : false
                  }
                  sx={{
                    width: column.width,
                    minWidth: column.width,
                    maxWidth: column.width,
                    overflow: "hidden",
                    whiteSpace: "nowrap"
                  }}
                >
                  {sortable ? (
                    <span
                      onClick={() => handleRequestSort(column.id)}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 2,
                        cursor: "pointer",
                        userSelect: "none",
                        fontWeight: 600,
                        fontSize: 13,
                        color: "#374151",
                      }}
                    >
                      {column.label}
                      {column.label && <UnfoldMore style={{ fontSize: "1rem", color: "var(--color-primary)", display: "block" }} />}
                    </span>
                  ) : (
                    <span style={{ fontWeight: 600, fontSize: 13, color: "#374151" }}>
                      {column.label}
                    </span>
                  )}
                </StyledTableCell>
              ))}
              {showActions && (
                <StyledTableCell style={{ textAlign: "right" }} />
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={
                    columns.length +
                    (selectable ? 1 : 0) +
                    (collapsible ? 1 : 0)
                  }
                  align="center"
                >
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    style={{ fontSize: "14px" }}
                  >
                    {"Bu listede görüntülenecek bir içerik bulunmamaktadır!"}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedData.map((row) => {
                const isItemSelected = isSelected(row.id);
                return (
                  <React.Fragment key={row.id}>
                    <SelectedRow hover selected={isItemSelected} sx={{ "& td": { fontSize: 13, borderBottom: "1px solid #f3f4f6", padding: "8px 10px" }, "&:hover td": { backgroundColor: "#faf9ff" } }}>
                      {selectable && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            color="primary"
                            checked={isItemSelected}
                            onChange={(event: any) =>
                              handleClick(event, row.id)
                            }
                          />
                        </TableCell>
                      )}
                      {collapsible && (
                        <TableCell padding="checkbox">
                          <IconButton
                            aria-label="expand row"
                            size="small"
                            onClick={() =>
                              setOpen({ ...open, [row.id]: !open[row.id] })
                            }
                            style={{ visibility: "hidden" }}
                          >
                            {open[row.id] ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </TableCell>
                      )}
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          sx={{
                            width: column.width,
                            minWidth: column.width,
                            maxWidth: column.width,
                            overflow: "hidden"
                          }}
                        >
                          <span
                            style={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "block",
                            }}
                          >
                            {column.render
                              ? column.render(row)
                              : column.id.toLowerCase().includes("date")
                              ? row[column.id]?.format?.("DD.MM.YYYY HH:mm") ?? row[column.id]
                              : row[column.id]}
                          </span>
                        </TableCell>
                      ))}
                      {showActions && (
                        <TableCell
                          style={{ textAlign: "right", padding: "1px" }}
                        >
                          <div style={{ marginRight: "20px" }}>
                            <Tooltip title={"Düzenle"}>
                              <IconButton>
                                <DriveFileRenameOutlineIcon
                                  style={{ color: "black" }}
                                  onClick={() => onRowClick && onRowClick(row)}
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={"Sil"}>
                              <IconButton
                                style={{ margin: "0 0px 0 15px" }}
                                disabled={!isDetailLoading}
                              >
                                <TrashIcon
                                  onClick={() => handleDeleteClick(row)}
                                />
                              </IconButton>
                            </Tooltip>
                          </div>
                        </TableCell>
                      )}
                    </SelectedRow>
                    {collapsible && open[row.id] && (
                      <TableRow>
                        <TableCell
                          colSpan={
                            columns.length +
                            (selectable ? 1 : 0) +
                            (collapsible ? 1 : 0)
                          }
                        >
                          <Collapse
                            in={open[row.id]}
                            timeout="auto"
                            unmountOnExit
                          >
                            <div>
                              {collapsibleContent && collapsibleContent(row)}
                            </div>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
          <ConfirmDialog
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            onConfirm={handleConfirmDelete}
          />
        </Table>

        {isPagination && (
          <TablePagination
            sx={{ overflowX: "auto" }}
            component="div"
            count={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={"Satır sayısı"}
            labelDisplayedRows={() => ``}
            ActionsComponent={(subProps) => (
              <CustomPagination
                page={subProps.page + 1}
                count={Math.ceil(totalCount / rowsPerPage)}
                onPageChange={(e: any, newPage: any) =>
                  subProps.onPageChange(e, newPage - 1)
                }
              />
            )}
          />
        )}
      </TableContainer >
    </>
  );
};

export default CustomTable;
